import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from '@phosphor-icons/react';
import { cn } from '../../../lib/utils';
interface FilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  /** Trigger wrapper element — used to position the desktop popover. */
  anchorRef: React.RefObject<HTMLElement>;
  /** Desktop popover width classes, e.g. "sm:w-80". */
  desktopWidthClass?: string;
  /** Optional custom footer (e.g. Apply / Reset for Budget). If provided,
   *  overrides the built-in resultCount CTA. */
  footer?: React.ReactNode;
  /** When set, FilterPanel renders a default sticky CTA "Vezi {resultCount} chiriași"
   *  at the bottom of the drawer. Ignored when `footer` is provided. */
  resultCount?: number;
  /** Optional secondary action shown next to the primary CTA (e.g. "Resetează"). */
  secondaryAction?: {
    label: string;
    onClick: () => void;
  } | null;
  children: React.ReactNode;
}
/**
 * Responsive filter panel shell.
 *
 * - Mobile (<sm): FULL-SCREEN drawer (inset-0) with sticky header (title + X)
 *   and sticky footer (primary CTA — defaults to "Vezi {N} chiriași" or a
 *   custom footer for input forms). Matches Airbnb / Booking / OLX mobile
 *   filter UX.
 * - Desktop (sm+): popover anchored below the trigger button.
 *
 * Rendered via portal to `document.body` so it escapes any ancestor containing
 * block (the marketplace sticky bar uses `backdrop-blur-md`, which promotes
 * itself to a containing block for `position: fixed` descendants).
 */
export function FilterPanel({
  isOpen,
  onClose,
  title,
  anchorRef,
  desktopWidthClass = 'sm:w-80',
  footer,
  resultCount,
  secondaryAction,
  children
}: FilterPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  // Track viewport size — sm breakpoint = 640px.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 639px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  // Lock body scroll on mobile while open.
  useEffect(() => {
    if (!isOpen || !isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen, isMobile]);
  // Measure anchor for desktop popover positioning.
  useEffect(() => {
    if (!isOpen || isMobile) return;
    if (!anchorRef.current) return;
    const measure = () => {
      if (anchorRef.current) setAnchorRect(anchorRef.current.getBoundingClientRect());
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [isOpen, isMobile, anchorRef]);
  // Desktop outside-click — close when click is outside both the trigger and the panel.
  useEffect(() => {
    if (!isOpen || isMobile) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, isMobile, anchorRef, onClose]);
  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;
  // Resolve which footer to render.
  // - A custom `footer` (e.g. Budget's Aplică/Resetează) is always rendered,
  //   on both mobile and desktop, because those drawers need an explicit
  //   submit step.
  // - The auto-generated `resultCount` CTA "Vezi N chiriași" is MOBILE ONLY.
  //   On desktop, the dropdown popovers are anchored to their triggers and
  //   feel like menus — selections apply live and the user dismisses by
  //   clicking outside. A primary CTA there would be redundant.
  const autoCTA = resultCount !== undefined ? <div className="flex items-center justify-between gap-3" data-id="element-2377">
        {secondaryAction ? <button type="button" onClick={secondaryAction.onClick} className="text-sm text-light-3 hover:text-light-1 transition-colors cursor-pointer" data-id="element-2378">
            {secondaryAction.label}
          </button> : <span data-id="element-2379" />}
        <button type="button" onClick={onClose} className="h-11 px-5 rounded-xl bg-lime-1 hover:bg-lime-2 text-dark-1 text-sm font-semibold transition-colors cursor-pointer" data-id="element-2380">
          Vezi {resultCount} {resultCount === 1 ? 'chiriaș' : 'chiriași'}
        </button>
      </div> : null;
  const mobileFooter = footer ?? autoCTA;
  const desktopFooter = footer ?? null;
  // ─── Mobile: full-screen drawer ────────────────────────────────────────────
  if (isMobile) {
    return createPortal(<>
        <div className="fixed inset-0 z-[100] bg-black/60 animate-in fade-in" onClick={onClose} aria-hidden data-id="element-2381" />
        <div ref={panelRef} className="fixed inset-0 z-[101] bg-dark-1 flex flex-col animate-in slide-in-from-bottom" role="dialog" aria-modal="true" aria-label={title} data-id="element-2382">
          {/* Sticky header */}
          <div className="shrink-0 border-b border-dark-4 bg-dark-1" data-id="element-2383">
            <div className="flex items-center justify-between px-4 h-14 pt-[env(safe-area-inset-top)]" data-id="element-2384">
              <h3 className="text-base font-semibold text-light-1" data-id="element-2385">{title}</h3>
              <button type="button" onClick={onClose} className="-mr-2 w-10 h-10 inline-flex items-center justify-center rounded-lg text-light-2 hover:text-light-1 hover:bg-dark-3 transition-colors" aria-label="Închide" data-id="element-2386">
                <X size={20} weight="bold" data-id="element-2387" />
              </button>
            </div>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto dark-scrollbar overscroll-contain" data-id="element-2388">
            {children}
          </div>

          {/* Sticky footer */}
          {mobileFooter && <div className="shrink-0 border-t border-dark-4 bg-dark-1 px-4 py-3 pb-[max(env(safe-area-inset-bottom),0.75rem)]" data-id="element-2389">
              {mobileFooter}
            </div>}
        </div>
      </>, document.body);
  }
  // ─── Desktop: anchored popover ─────────────────────────────────────────────
  if (!anchorRect) return null;
  return createPortal(<div ref={panelRef} className={cn('fixed z-[100] bg-dark-2 border border-white/15 rounded-xl flex flex-col animate-in fade-in slide-in-from-top-2 shadow-xl overflow-hidden', desktopWidthClass)} style={{
    top: anchorRect.bottom + 8,
    left: anchorRect.left
  }} role="dialog" aria-label={title} data-id="element-2390">
      <div className="flex-1 overflow-y-auto dark-scrollbar" data-id="element-2391">{children}</div>
      {desktopFooter && <div className="shrink-0 border-t border-dark-3 px-4 py-3" data-id="element-2392">
          {desktopFooter}
        </div>}
    </div>, document.body);
}